import { formatBytes, type LintReport, type Params } from '@jsonfmt/core';

import type { DocumentSnapshot, ToneOfVoice } from '@/features/types';

import { safeFixableCount } from './problemList';

/**
 * The status pill, as data — docs/ARCHITECTURE.md §3 (`features/lint/statusModel.ts`),
 * docs/DESIGN.md §6.11, ADR-0009.
 *
 * Pure, DOM-free, bare-Node tested. `StatusPill.tsx` renders this and decides nothing.
 *
 * The pill is the persistent, glanceable verdict a toast can never be, and docs/DESIGN.md
 * §6.11 is explicit about what it may and may not say: it carries EDITOR METADATA, and the
 * document's facts (size, lines, depth, keys) live only in the colophon — so a row of
 * numbers can never be mistaken for a validity signal.
 */

export type StatusKind =
  /** Nothing has been typed. Not a verdict; the state the application starts in. */
  | 'empty'
  /** `reportVersion < version`: the report is not about this text (ADR-0009). */
  | 'stale'
  | 'valid'
  | 'problems'
  /** Past the hard size limit: the editor is off, and so is live analysis. */
  | 'blocked';

/** The glyph shape, so severity survives greyscale and `forced-colors` (DESIGN §6.4). */
export type StatusGlyph = 'check' | 'dot' | 'triangle' | 'ring' | 'none';

/**
 * One translatable fragment of the pill's sentence.
 *
 * Fragments rather than one key because the mockup's invalid state reads
 * "2 errors, 1 warning" and `locales/en/common.json` ships exactly the three counted
 * fragments (`editor.status.errors` / `warnings` / `notes`) to build it from. Using the
 * BASE key plus `{ count }` is what lets i18next pluralise by the rules of the language
 * (ADR-0018); `_one` / `_other` are never named here.
 */
export interface StatusSegment {
  readonly key: string;
  readonly params: Params;
}

export interface StatusModel {
  readonly kind: StatusKind;
  /** Errors + warnings + notes. `0` for every kind that is not `'problems'`. */
  readonly count: number;
  readonly tone: ToneOfVoice;
  readonly glyph: StatusGlyph;
  /** Rendered left to right, joined by the component. Empty for `'stale'`. */
  readonly segments: readonly StatusSegment[];
  /** How many problems "Fix all" would actually close — SAFE fixes only (ADR-0012). */
  readonly fixable: number;
}

const EMPTY: StatusModel = {
  kind: 'empty',
  count: 0,
  tone: 'info',
  glyph: 'none',
  segments: [{ key: 'editor.status.empty', params: {} }],
  fixable: 0,
};

/**
 * No label, and that is the whole point.
 *
 * ADR-0009 requires a NEUTRAL state rather than an out-of-date verdict while
 * `reportVersion < version`. `locales/en/common.json` has no word for "checking", and the
 * locale file is shared with three peer stages mid-flight, so the pill renders a muted `…`
 * plus `aria-busy` instead of inventing a key. Named in the report as a gap, not hidden.
 */
const STALE: StatusModel = {
  kind: 'stale',
  count: 0,
  tone: 'info',
  glyph: 'none',
  segments: [],
  fixable: 0,
};

const VALID: StatusModel = {
  kind: 'valid',
  count: 0,
  tone: 'success',
  glyph: 'check',
  segments: [{ key: 'editor.status.valid', params: {} }],
  fixable: 0,
};

/**
 * `LintReport` → the pill.
 *
 * The empty document is handled HERE rather than by the caller, because the engine reports
 * it as data: an empty buffer yields exactly one `syntax/empty-document` diagnostic, whose
 * severity is `info` precisely so that the pill is not red on the application's first paint
 * (`core/lint/codes.ts`). A model built from `counts` alone would announce "1 note" over an
 * empty editor.
 */
export function statusFromReport(report: LintReport | null): StatusModel {
  if (report === null) return STALE;

  const { errors, warnings, infos } = report.counts;
  const total = errors + warnings + infos;
  if (total === 0) return VALID;

  const onlyEmptyDocument =
    report.diagnostics.length > 0 &&
    report.diagnostics.every((d) => d.code === 'syntax/empty-document');
  if (onlyEmptyDocument) return EMPTY;

  const segments: StatusSegment[] = [];
  if (errors > 0) segments.push({ key: 'editor.status.errors', params: { count: errors } });
  if (warnings > 0) segments.push({ key: 'editor.status.warnings', params: { count: warnings } });
  if (infos > 0) segments.push({ key: 'editor.status.notes', params: { count: infos } });

  // The tone and the glyph follow the WORST severity present. A document with two errors and
  // one note is red, not blue — the pill grades a document, it does not average it.
  const tone: ToneOfVoice = errors > 0 ? 'error' : warnings > 0 ? 'warning' : 'info';
  const glyph: StatusGlyph = errors > 0 ? 'dot' : warnings > 0 ? 'triangle' : 'ring';

  return {
    kind: 'problems',
    count: total,
    tone,
    glyph,
    segments,
    fixable: safeFixableCount(report),
  };
}

/**
 * The form the shell uses: the report, plus the three things only the snapshot knows —
 * emptiness, the hard size limit, and staleness.
 *
 * Order is load-bearing. `blocked` outranks everything (there is no live analysis at that
 * size, so any verdict would be stale by construction); `empty` outranks `stale` so the
 * first paint says "Empty" instead of blinking an ellipsis at a user who has typed nothing.
 */
export function statusOf(doc: DocumentSnapshot): StatusModel {
  if (doc.size === 'over-limit') {
    return {
      kind: 'blocked',
      count: 0,
      tone: 'error',
      glyph: 'dot',
      segments: [{ key: 'editor.status.blocked', params: { size: formatBytes(doc.bytes) } }],
      fixable: 0,
    };
  }
  if (doc.text.length === 0) return EMPTY;
  if (doc.report === null || doc.reportVersion < doc.version) return STALE;
  return statusFromReport(doc.report);
}
